import { ArrowRight, Star, Sparkles, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { HERO_IMAGE, LOGO_URL, GMB_URL } from "../mock";
import { openExternal } from "../lib/openExternal";

export default function Hero() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden bg-jester-deep"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Ceramic coated luxury car in Jester Werks studio Mississauga"
          width="1920"
          height="1080"
          fetchpriority="high"
          decoding="async"
          className="w-full h-full object-cover scale-105 opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#120127] via-[#120127]/85 to-[#120127]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#120127]/30 via-transparent to-[#120127]" />
      </div>

      <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-[#8D39EE]/15 blur-[140px]" />
      <div className="absolute top-1/4 -left-20 w-[500px] h-[500px] rounded-full bg-[#3C1478]/40 blur-[120px]" />
      <div className="absolute top-10 right-10 w-72 h-72 rounded-full bg-[#A8E000]/8 blur-[100px]" />

      <img
        src={LOGO_URL}
        alt=""
        aria-hidden="true"
        className="hidden lg:block absolute right-[5%] top-1/2 -translate-y-1/2 w-[440px] xl:w-[500px] opacity-90 drop-shadow-[0_0_60px_rgba(141,57,238,0.6)] spin-slow"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-32 pb-28">
        <div className="max-w-4xl">
          {/* Top trust pill. bigger and bolder with #1 stamp inline */}
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-3 pl-1.5 pr-5 py-1.5 rounded-full border border-acid/50 bg-acid/10 backdrop-blur-sm mb-7 fade-up"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-acid text-[#120127] font-stencil text-sm">
              #1
            </span>
            <span className="text-[11px] md:text-xs font-label uppercase tracking-[0.3em] text-acid">
              On Google &middot; Mississauga &amp; Vaughan
            </span>
          </div>

          {/* New headline. 3 stacked MOSTs with rotating accent colors */}
          <h1
            data-testid="hero-headline"
            className="font-display leading-[0.85] tracking-tight fade-up"
            style={{ animationDelay: "80ms" }}
          >
            <span className="block text-[14vw] md:text-[6.4rem] xl:text-[7rem] text-white">
              MOST FAMOUS.
            </span>
            <span className="block text-[14vw] md:text-[6.4rem] xl:text-[7rem] text-jester">
              MOST TRANSPARENT.
            </span>
            <span className="block text-[14vw] md:text-[6.4rem] xl:text-[7rem] text-acid">
              MOST REVIEWED.
            </span>
          </h1>

          {/* Marker subtitle. handwritten acid-green tag */}
          <div
            className="mt-4 fade-up"
            style={{ animationDelay: "180ms" }}
          >
            <span className="font-marker text-white/85 text-2xl md:text-4xl leading-tight inline-block">
              ceramic coating studio in
            </span>{" "}
            <span className="font-marker text-acid text-2xl md:text-4xl leading-tight inline-block">
              Mississauga
            </span>{" "}
            <span className="font-marker text-white/85 text-2xl md:text-4xl leading-tight inline-block">
              &amp;
            </span>{" "}
            <span className="font-marker text-acid text-2xl md:text-4xl leading-tight inline-block">
              Vaughan.
            </span>
          </div>

          {/* Inline trust strip. visible flex */}
          <div
            data-testid="hero-trust-strip"
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 fade-up"
            style={{ animationDelay: "260ms" }}
          >
            <div className="flex items-center gap-2 text-white/85">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4" style={{ color: "#FDC814", fill: "#FDC814" }} />
                ))}
              </div>
              <span className="text-sm font-semibold">5.0</span>
              <span className="text-sm text-white/55">·</span>
              <span className="text-sm">
                <strong className="text-acid">250+</strong> Google reviews
              </span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-white/15" />
            <div className="text-sm text-white/85">
              <strong className="text-acid">1,500+</strong> cars coated
            </div>
            <div className="hidden sm:block w-px h-5 bg-white/15" />
            <div className="text-sm text-white/85">
              <strong className="text-acid">1M+</strong> monthly views
            </div>
            <div className="hidden md:block w-px h-5 bg-white/15" />
            <div className="hidden md:flex items-center gap-1.5 text-sm text-white/85">
              <MapPin className="w-3.5 h-3.5 text-acid" />
              5156 Oscar Peterson Blvd
            </div>
          </div>

          {/* CTA */}
          <div
            className="mt-9 fade-up"
            style={{ animationDelay: "360ms" }}
          >
            <Button
              asChild
              size="lg"
              data-testid="hero-cta-primary"
              className="bg-acid hover:bg-[#bff020] text-[#120127] font-label tracking-[0.2em] rounded-full px-9 h-13 shadow-[0_0_40px_rgba(168,224,0,0.45)]"
            >
              <a href="/ceramic-coating-mississauga" className="flex items-center gap-2">
                See Ceramic Packages <ArrowRight className="w-4 h-4" />
              </a>
            </Button>
          </div>

          {/* Reviewer pile linked to GMB */}
          <a
            href={GMB_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-gmb-link"
            className="mt-10 inline-flex items-center gap-4 fade-up group cursor-pointer"
            style={{ animationDelay: "480ms" }}
            onClick={(e) => { e.preventDefault(); openExternal(GMB_URL); }}
          >
            <div className="flex -space-x-2">
              {["K", "A", "E", "M"].map((c, i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full border-2 border-[#120127] bg-gradient-to-br from-[#3C1478] to-[#1a0938] flex items-center justify-center text-[11px] font-bold"
                >
                  {c}
                </div>
              ))}
            </div>
            <div className="text-xs text-white/55 group-hover:text-acid transition-colors uppercase tracking-[0.2em] font-label">
              Read what {" "}
              <span className="text-acid">250+ Mississauga drivers</span>
              {" "} are saying →
            </div>
          </a>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 border-y border-white/10 bg-[#0c0119]/80 backdrop-blur-md overflow-hidden">
        <div className="marquee-track flex whitespace-nowrap py-4 text-white/40 uppercase tracking-[0.3em] text-xs font-label">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 px-6">
              {[
                "#1 on Google · Mississauga",
                "Most-Reviewed in GTA",
                "5-Year Warranty",
                "250+ Google Reviews",
                "1,500+ Cars Coated",
                "20K+ Instagram Followers",
                "1M+ Monthly Views",
                "Hand Applied",
                "Climate-Controlled Bay",
                "Owner Operated",
              ].map((t, i) => (
                <span key={i} className="flex items-center gap-12">
                  <span className="text-acid">♦</span>
                  <span className="text-white/70">{t}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
